import { ThreeEvent } from '@react-three/fiber'
import {
  MapFeatureType,
  useFeatureInteractionContext,
} from 'features/siteMap/contexts/FeatureInteractionContext'
import {
  ExtraLabelDisplayFn,
  UnitDisplayPropertiesFn,
} from 'features/siteMapViz'
import isNil from 'lodash/isNil'
import React, { FC, useCallback, useMemo } from 'react'
import {
  SiteFeature,
  SiteFeatureLabel,
  SiteFeaturePolygon,
  SiteFeaturePrimitiveBox,
  SiteFeaturePrimitiveCylinder,
  SiteFeaturePrimitiveSphere,
} from 'types'
import { colorAsRGBString } from 'utils/colorHelpers'

import { TilesetMapFeatureBox3D } from './TilesetMapFeatureBox3D'
import { TilesetMapFeatureCylinder3D } from './TilesetMapFeatureCylinder3D'
import { TilesetMapFeatureLabel3D } from './TilesetMapFeatureLabel3D'
import { TilesetMapFeaturePolygon3D } from './TilesetMapFeaturePolygon3D'
import { TilesetMapFeatureSphere3D } from './TilesetMapFeatureSphere3D'

interface TilesetMapFeatureDispatchProps {
  feature: SiteFeature
  featureDisplayName: string | undefined
  key: string
  visible: boolean
  getUnitDisplayProperties: UnitDisplayPropertiesFn
  getExtraLabelDisplay: ExtraLabelDisplayFn
}

export const TilesetMapFeatureDispatch: FC<TilesetMapFeatureDispatchProps> = (
  props
) => {
  const { onFeatureClick } = useFeatureInteractionContext()
  const { color: featureColorBase, pattern: featurePattern } =
    props.getUnitDisplayProperties(props.feature.unit_id)
  const featureColorNoAlpha = colorAsRGBString(featureColorBase)

  const extraLabelDisplay = props.getExtraLabelDisplay(props.feature.unit_id)

  const onFeatureClickHandler = useCallback(
    (e: ThreeEvent<MouseEvent>): ReturnType<typeof onFeatureClick> => {
      return onFeatureClick(
        e,
        isNil(props.feature.unit_id)
          ? {
              featureID: props.feature.id,
              type: MapFeatureType.Unknown,
            }
          : {
              unitID: props.feature.unit_id,
              type: MapFeatureType.Unit,
            }
      )
    },
    [props.feature, onFeatureClick]
  )

  return useMemo(() => {
    switch (props.feature.kind) {
      case 'polygon':
        return (
          <TilesetMapFeaturePolygon3D
            key={props.key}
            polygon={props.feature as SiteFeaturePolygon}
            color={featureColorNoAlpha}
            visible={props.visible}
            extraLabelDisplay={extraLabelDisplay}
          />
        )
      case 'label':
        return (
          <TilesetMapFeatureLabel3D
            key={props.key}
            label={props.feature as SiteFeatureLabel}
            visible={props.visible}
          />
        )
      /** Asset Features */
      case 'box':
        return (
          <TilesetMapFeatureBox3D
            key={props.key}
            box={props.feature as SiteFeaturePrimitiveBox}
            name={props.featureDisplayName}
            color={featureColorNoAlpha}
            visible={props.visible}
            pattern={featurePattern}
            onClick={onFeatureClickHandler}
            extraLabelDisplay={extraLabelDisplay}
          />
        )
      case 'sphere':
        return (
          <TilesetMapFeatureSphere3D
            key={props.key}
            sphere={props.feature as SiteFeaturePrimitiveSphere}
            name={props.featureDisplayName}
            color={featureColorNoAlpha}
            visible={props.visible}
            pattern={featurePattern}
            onClick={onFeatureClickHandler}
            extraLabelDisplay={extraLabelDisplay}
          />
        )
      case 'cylinder':
        return (
          <TilesetMapFeatureCylinder3D
            key={props.key}
            cylinder={props.feature as SiteFeaturePrimitiveCylinder}
            name={props.featureDisplayName}
            color={featureColorNoAlpha}
            visible={props.visible}
            pattern={featurePattern}
            onClick={onFeatureClickHandler}
            extraLabelDisplay={extraLabelDisplay}
          />
        )
    }
  }, [
    extraLabelDisplay,
    featureColorNoAlpha,
    featurePattern,
    onFeatureClickHandler,
    props.feature,
    props.key,
    props.visible,
    props.featureDisplayName,
  ])
}
